import React from 'react';
import { useLocation } from 'react-router-dom';
import {
  Button, Alert, ConfirmDialog, useToast,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter,
} from '@parsina/framework';
import { Plus, Mic } from 'lucide-react';

import { CT } from '../../../constants/theme';
import { useTranslation } from '../../../i18n/I18nProvider.jsx';
import { HISTORY_SECTIONS, groupBySection } from '../../../lib/history.js';
import { errorMessageKey } from '../../../lib/bffClient.js';
import RecordSection from '../../../components/common/RecordSection.jsx';
import RecordListSheet from '../../../components/common/RecordListSheet.jsx';
import { HistoryEntryCard } from '../../../components/history/HistorySection.jsx';
import EntryEditor from '../../../components/history/EntryEditor.jsx';
import VoicePanel from '../../../components/history/VoicePanel.jsx';
import { useEntryEditor } from '../../../components/history/historyForm.js';
import { useCaseVoice } from '../../../hooks/useCaseVoice.js';
import { useMe } from '../../../hooks/useMe.js';

// شرح حال — the seven sections, as the student writes them. core.md §7.4.
//
// Each section is a collapsible `RecordSection` whose header carries its count
// and its «افزودن» button, so an EMPTY section is an invitation rather than a
// blank box: a student who has written four sections should see the other three
// as three things left to do, not as three empty cards.
//
// The order is the CLINICAL order (`HISTORY_SECTIONS`), which is also the order
// the BFF's prompt compiler reads them in — what the student sees down the page
// is what the assistant reads down the prompt.
//
// ── «دیکته» is a sheet, and it writes nothing by itself ───────────────────
//
// `VoicePanel` runs the browser's recogniser, sends the TRANSCRIPT (never
// audio), and produces a DRAFT. The student ticks what they keep and it lands
// through the same `addCaseHistory` they would have used by hand, stamped
// `source: "voice"` (core.md §3.4). Nothing on this tab can put a sentence into
// the record that the student did not confirm.

export default function HistoryTab({
  caseId, entries, encounters = [], addHistory, updateEntry, deleteEntry,
}) {
  const { t, formatNumber } = useTranslation();
  const toast = useToast();
  const { balance } = useMe();
  const voice = useCaseVoice(caseId);
  const location = useLocation();

  // The create flow lands here with `hint: 'created'` in router state (core.md
  // §7.3): for THAT first visit the hint is «شرح حال را بنویسید یا دیکته
  // کنید» — the two ways to fill an empty history — and the normal line after.
  // Read ONCE, at mount: the state belongs to the history entry the create
  // pushed, so a tab change (a new entry, no state) already forgets it, and a
  // lazy initialiser means a re-render cannot re-read it either.
  const [justCreated] = React.useState(() => location.state?.hint === 'created');

  const editor = useEntryEditor({ entries, addHistory, updateEntry });
  const [voiceOpen, setVoiceOpen] = React.useState(false);
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);

  const bySection = React.useMemo(() => groupBySection(entries), [entries]);

  // An entry stamped with a visit shows «ویزیت N». The entry carries the
  // encounter's ID and the record carries its number, so the chip is resolved
  // here rather than asked for: an entry whose visit was cancelled keeps the
  // entry (core.md §5.1) and simply loses the chip, which is exactly right —
  // the fact survives, the grouping does not.
  const seqById = React.useMemo(() => {
    const map = new Map();
    for (const enc of encounters || []) map.set(enc.id, enc.seq);
    return map;
  }, [encounters]);
  const seqOf = React.useCallback(
    (entry) => (entry?.encounterId ? seqById.get(entry.encounterId) ?? null : null),
    [seqById],
  );

  const onSave = async () => {
    try {
      const saved = await editor.save();
      if (saved) toast.success(t('history.saved'));
    } catch (err) {
      toast.error(t(errorMessageKey(err)));
    }
  };

  const onDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteEntry(deleteTarget.id);
      toast.success(t('history.deleted'));
      setDeleteTarget(null);
    } catch (err) {
      toast.error(t(errorMessageKey(err)));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-testid="record-history">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <p
          style={{ margin: 0, fontSize: 12.5, color: CT.mute, flex: 1 }}
          data-testid={justCreated ? 'history-hint-created' : 'history-hint'}
        >
          {justCreated ? t('patients.createdHint') : t('record.history.hint')}
        </p>
        <Button variant="outline" onClick={() => setVoiceOpen(true)} data-testid="history-dictate">
          <Mic size={15} aria-hidden />
          {t('record.history.dictate')}
        </Button>
      </div>

      {HISTORY_SECTIONS.map(({ id, labelKey, Icon }) => {
        const rows = bySection[id] || [];
        return (
          <RecordSection
            key={id}
            id={id}
            title={t(labelKey)}
            icon={Icon}
            count={rows.length ? formatNumber(rows.length) : null}
            action={(
              <Button
                variant="ghost"
                size="sm"
                onClick={() => editor.openNew(id)}
                data-testid={`history-add-${id}`}
              >
                <Plus size={14} aria-hidden />
                {t('history.add')}
              </Button>
            )}
            data-testid={`history-section-${id}`}
          >
            {rows.length ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {rows.map((entry) => (
                  <HistoryEntryCard
                    key={entry.id}
                    entry={entry}
                    visitSeq={seqOf(entry)}
                    onEdit={editor.openExisting}
                    onDelete={setDeleteTarget}
                  />
                ))}
              </div>
            ) : (
              // An empty section is EMPTY, not broken — and the recovery is one
              // click away in the header, so this line explains rather than
              // repeating the button.
              <p style={{ margin: '4px 0', fontSize: 12.5, color: CT.mute }}>
                {t('history.empty.body')}
              </p>
            )}
          </RecordSection>
        );
      })}

      {/* The editor is a Dialog on both surfaces: it is a form with a textarea
          and, for vitals and labs, a grid of rows — a bottom sheet with a
          keyboard over it leaves nothing of either. */}
      <Dialog open={editor.open} onOpenChange={(next) => { if (!next) editor.close(); }}>
        <DialogContent size="lg" layout="frame" data-testid="entry-editor-dialog">
          <DialogHeader>
            <DialogTitle>
              {editor.editing ? t('common.actions.edit') : t('history.add')}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            {editor.error && (
              <Alert variant="warning" style={{ marginBottom: 12 }}>{t(editor.error)}</Alert>
            )}
            <EntryEditor
              values={editor.form}
              onChange={editor.change}
              disabled={editor.saving}
            />
          </DialogBody>
          <DialogFooter style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <Button onClick={onSave} disabled={editor.saving} data-testid="entry-save">
              {editor.saving ? t('common.actions.saving') : t('common.actions.save')}
            </Button>
            <Button variant="outline" onClick={editor.close} disabled={editor.saving}>
              {t('common.actions.cancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RecordListSheet
        open={voiceOpen}
        onClose={() => setVoiceOpen(false)}
        title={t('record.history.dictate')}
        width={520}
        data-testid="voice-sheet"
      >
        <VoicePanel
          entries={entries}
          assistant={voice}
          addHistory={addHistory}
          balance={balance}
        />
      </RecordListSheet>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(next) => { if (!next) setDeleteTarget(null); }}
        title={t('history.delete.title')}
        description={t('history.delete.body')}
        confirmLabel={t('common.actions.delete')}
        cancelLabel={t('common.actions.cancel')}
        destructive
        loading={deleting}
        onConfirm={onDelete}
        confirmTestId="entry-delete-confirm"
      />
    </div>
  );
}
